/**
 * System Admin ▸ System Emails — who receives the staff-facing system emails.
 *
 * ONE list for all of them (CEO, 2026-09-17): the people checked receive every
 * system email — licence approval requests, reseller quotes, purchase orders,
 * reseller applications — and the people not checked receive none. NOBODY
 * checked means NOBODY receives them: there is no "every super admin" fallback
 * any more (CEO, 2026-09-17 — an all-unchecked page still mailed five admins).
 *
 * Licence approval requests go only to the CHECKED SUPER ADMINS, because nobody
 * else can approve one.
 *
 * The list, the eligibility rule and the email kinds live in crm-api
 * (`SystemEmails::Recipients`), because crm-api sends the mail. These are the
 * wire types the BFF passes through and the page renders, plus the warnings
 * the page shows (warnings, never a block — any list may be saved).
 */

/** The dashboard roles a recipient may hold — "admins or salespeople". */
export const SYSTEM_EMAIL_ELIGIBLE_ROLES = ["super_admin", "sales_manager"] as const;
export type SystemEmailEligibleRole = (typeof SYSTEM_EMAIL_ELIGIBLE_ROLES)[number];

export interface SystemEmailCandidate {
  /** crm-api user id — the mirror of a dashboard account. */
  id: number;
  name: string;
  email: string;
  dashboard_role: SystemEmailEligibleRole;
}

/** One kind of system email the list covers — shown so the admin knows what they are choosing for. */
export interface SystemEmailKind {
  key: string;
  label: string;
  description: string;
}

export interface SystemEmailSettings {
  /** The checked people. Empty = nobody checked = no system email is sent. */
  user_ids: number[];
  /** Who receives the system emails right now — exactly the checked, still-eligible people. */
  effective_emails: string[];
  /** Who receives licence approval requests — the checked super admins only. */
  licence_approval_emails: string[];
  /** What the one list covers. */
  emails: SystemEmailKind[];
  candidates: SystemEmailCandidate[];
}

export const SYSTEM_EMAIL_NOBODY_WARNING = "Nobody is checked, so no system emails are sent.";
export const SYSTEM_EMAIL_NO_SUPER_ADMIN_WARNING =
  "Licence approval requests only go to super admins who can approve them — none is checked, so licences waiting for approval will not be emailed.";

/**
 * What the page should warn about for a draft list, or null when nothing. A
 * WARNING, not a refusal: crm-api accepts any list of eligible people,
 * including an empty one and one with no super admin.
 */
export function systemEmailSelectionWarning(
  userIds: readonly number[],
  candidates: readonly SystemEmailCandidate[],
): string | null {
  if (userIds.length === 0) return SYSTEM_EMAIL_NOBODY_WARNING;
  const picked = candidates.filter((c) => userIds.includes(c.id));
  if (picked.some((c) => c.dashboard_role === "super_admin")) return null;
  return SYSTEM_EMAIL_NO_SUPER_ADMIN_WARNING;
}
